import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Box,
  TableRow,
  TextField,
  FormHelperText,
  Tooltip,
  FormControl,
  IconButton,
  InputAdornment,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Warning from "../../../icons/Warning";
import Delete from "../../../icons/Delete";
import UndoIcon from "../../../icons/Undo";
import SearchIcon from "../../../icons/Search";
import UndoButton from "../../components/Undo";
import Link from "../../components/Link";
import { DeleteButton } from "../../components/SectionComponents";
import { TableCell } from "../../components/TableCell";
import {
  useSpecificationStateStore,
  type ChangeTarget,
  type Change,
  isItem,
} from "./specificationState";
import {
  getRequiredBomItemTypeConstraints,
  getRequiredBomTemplateConstraints,
  getAggregateLimitConstraints,
  useViolationSummary,
} from "./validationState";

// Helper function to check if a BOM item has any edits
export const hasItemEdits = (item: ItemWithBOM): boolean => {
  // Check if item has attribute changes
  if (item.item_changes && item.item_changes.length > 0) {
    return true;
  }

  // Check if item has BOM changes
  if (item.bom_changes && item.bom_changes.length > 0) {
    return true;
  }

  // Check if item has process changes (additions/deletions)
  if (item.process_changes && item.process_changes.length > 0) {
    return true;
  }

  // Check if any processes have parameter changes
  if (item.processes && item.processes.length > 0) {
    const hasParameterChanges = item.processes.some(
      (proc) => proc.process_changes && proc.process_changes.length > 0
    );
    if (hasParameterChanges) {
      return true;
    }
  }

  // Recursively check if any child items have edits
  if (item.bom && item.bom.length > 0) {
    return item.bom.some((bomItem) => hasItemEdits(bomItem));
  }

  return false;
};

export const StyledTableRow = styled(TableRow, {
  shouldForwardProp: (prop) => prop !== "isDeleted" && prop !== "isAdded",
})<{
  isDeleted?: boolean;
  isAdded?: boolean;
}>(({ theme, isDeleted, isAdded }) => ({
  transition: "background-color 0.2s ease",
  "&:hover": {
    backgroundColor: "#f5f5f5",
  },
  backgroundColor: isAdded ? "rgba(25, 118, 210, 0.08)" : "#fff",
  opacity: isDeleted ? 0.5 : 1,
  textDecoration: isDeleted ? "line-through" : "none",
}));

export type ChildRowProps = {
  child: BOMItem;
  // Line to display (defaults to the stored `child.line_number`).
  lineNumber?: number | null;
  // Hint of the BOM line slot the child occupies (defaults to `child.hint`).
  slotHint?: string | null;
  // The displayed line is a preview of what FormSpec assigns on save.
  isTentativeLine?: boolean;
  currentPath: string;
  onItemClick: (item: ItemWithBOM) => void;
  onPartNumberReplace: (child: ItemWithBOM, bomIndex: number) => void;
  onDelete: (index: number) => void;
  onUndo: (index: number) => void;
  isBomItemDeleted: (index: number) => boolean;
  isBomItemAdded: (index: number) => boolean;
  index: number;
  canDelete: boolean;
  deleteDisabledReason?: string;
  constraints: ConstraintsResponse | null;
  parentItemUuid: string | null;
  readOnly?: boolean;
};

export const ChildRow: React.FC<ChildRowProps> = ({
  child,
  lineNumber,
  slotHint,
  isTentativeLine = false,
  currentPath,
  onItemClick,
  onPartNumberReplace,
  canDelete,
  deleteDisabledReason,
  onDelete,
  onUndo,
  isBomItemDeleted,
  isBomItemAdded,
  index,
  constraints,
  parentItemUuid,
  readOnly = false,
}) => {
  const childItem = child.item;
  const isDeleted = isBomItemDeleted(index);
  const _isAdded = isBomItemAdded(index);
  const isEdited = hasItemEdits(child);

  // Create change target for this BOM quantity using path-based approach
  const changeTarget: ChangeTarget = {
    type: "bom_quantity",
    path: currentPath,
    bomIndex: index,
  };

  const trackChange = useSpecificationStateStore((state) => state.trackChange);
  const getChange = useSpecificationStateStore((state) => state.getChange);

  const change = getChange(changeTarget);
  const isChanged = change !== null;
  const storeQuantity =
    change && change.currentValue !== undefined
      ? (change.currentValue as number)
      : child.quantity || 1;

  // Get violations for this BOM item by checking constraint UUIDs that apply to it
  const violationSummary = useViolationSummary();

  // Find violations on the parent path that apply to this specific BOM item
  const parentViolations =
    violationSummary.getItemViolationsByPath(currentPath);

  // Filter violations to only those that apply to this specific item
  const itemViolations = useMemo(() => {
    if (!isItem(child.item)) return [];

    const templateUuid = child.item.base_template?.template_definition_uuid;
    const itemTypeUuid = child.item.item_type?.uuid;

    return parentViolations.filter((v) => {
      // Check if this violation is for a required_bom_template constraint matching this item
      if (v.constraint_type === "required_bom_template" && templateUuid) {
        const constraint = constraints
          ? getRequiredBomTemplateConstraints(
              constraints,
              parentItemUuid || ""
            ).find((c: any) => (c as any).config.uuid === v.constraint_uuid)
          : null;
        return (
          constraint &&
          (constraint as any).target_template_uuid === templateUuid
        );
      }

      // Check if this violation is for a required_bom_item_type constraint matching this item
      if (v.constraint_type === "required_bom_item_type" && itemTypeUuid) {
        const constraint = constraints
          ? getRequiredBomItemTypeConstraints(
              constraints,
              parentItemUuid || ""
            ).find((c: any) => (c as any).config.uuid === v.constraint_uuid)
          : null;
        return (
          constraint &&
          (constraint as any).target_item_type_uuid === itemTypeUuid
        );
      }

      // Check if this violation is for an aggregate_limit constraint that affects this item
      if (v.constraint_type === "aggregate_limit") {
        // Only show if this item matches the constraint's field_identifiers
        if (!isItem(child.item)) return false;

        const constraint = constraints
          ? getAggregateLimitConstraints(
              constraints,
              parentItemUuid || ""
            ).find((c: any) => (c as any).config.uuid === v.constraint_uuid)
          : null;

        if (!constraint) return false;

        const config = (constraint as any).config;
        const fieldIdentifiers = config?.field_identifiers || [];

        // Check if this item matches any of the field identifiers
        const childTemplateUuid =
          child.item.base_template?.template_definition_uuid;
        const childItemTypeUuid = child.item.item_type?.uuid;
        const childItemTypeName = child.item.item_type?.name;

        const childIdentifiers = [
          childTemplateUuid?.toLowerCase(),
          childItemTypeUuid?.toLowerCase(),
          childItemTypeName?.toLowerCase(),
        ].filter((id): id is string => !!id);

        const normalizedFieldIdentifiers = fieldIdentifiers.map((id: string) =>
          id.toLowerCase()
        );

        return childIdentifiers.some((id) =>
          normalizedFieldIdentifiers.includes(id)
        );
      }

      return false;
    });
  }, [parentViolations, child.item, constraints, parentItemUuid]);

  const hasViolations = itemViolations.length > 0;
  const violationMessage =
    itemViolations.length > 0 ? itemViolations[0].message : null;

  // Local state for input to allow intermediate invalid values
  const [localQuantity, setLocalQuantity] = useState(String(storeQuantity));
  const prevStoreQuantityRef = useRef(storeQuantity);

  // Sync local state when store value changes from outside
  useEffect(() => {
    if (prevStoreQuantityRef.current !== storeQuantity) {
      setLocalQuantity(String(storeQuantity));
      prevStoreQuantityRef.current = storeQuantity;
    }
  }, [storeQuantity]);

  const displayLine = lineNumber !== undefined ? lineNumber : child.line_number;
  const hint = slotHint !== undefined ? slotHint : child.hint;
  const storedLine = child.line_number ?? null;
  const lineTooltip = [
    hint ? `Slot: ${hint}` : "",
    isTentativeLine
      ? `Line assigned on save${
          storedLine !== null ? `; currently line ${storedLine}` : ""
        }`
      : "",
  ]
    .filter(Boolean)
    .join(". ");

  return (
    <StyledTableRow
      key={childItem.arena_guid || index}
      isDeleted={isDeleted}
      isAdded={_isAdded}
    >
      <TableCell sx={{ width: "70px", color: "#666" }}>
        <Tooltip title={lineTooltip} placement="top" arrow>
          <span
            style={isTentativeLine ? { fontStyle: "italic" } : undefined}
            aria-label={
              isTentativeLine ? `Pending line ${displayLine ?? "-"}` : undefined
            }
          >
            {displayLine ?? "-"}
            {isTentativeLine && "*"}
          </span>
        </Tooltip>
      </TableCell>
      <TableCell>
        {hasViolations && (
          <Warning
            style={{
              fontSize: "16px",
              color: "#ff9800",
              marginRight: "8px",
            }}
          />
        )}
        <Link
          href="#"
          onClick={(event) => {
            event.preventDefault();
            onItemClick(child);
          }}
        >
          {childItem.name}
        </Link>
        {hint && (
          <Typography
            component="div"
            sx={{ fontSize: "12px", color: "#757575", fontStyle: "italic" }}
          >
            {hint}
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <span>
            {isEdited ? "Pending" : childItem.arena_part_number || "-"}
          </span>
          <Tooltip title="Replace part number" placement="top" arrow>
            <span>
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  onPartNumberReplace(child, index);
                }}
                aria-label={`Replace part number for ${childItem.name}`}
                disabled={readOnly}
              >
                <SearchIcon sx={{ fontSize: "16px" }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </TableCell>
      <TableCell>{childItem.item_type_name || "-"}</TableCell>
      <TableCell
        onClick={(event: React.MouseEvent) => {
          event.stopPropagation();
        }}
        sx={{ width: "150px", padding: "8px" }}
      >
        <FormControl
          error={hasViolations}
          fullWidth
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <TextField
            size="small"
            variant="outlined"
            type="number"
            value={localQuantity}
            error={hasViolations}
            onChange={(e) => {
              const value = e.target.value;
              setLocalQuantity(value);

              // Propagate changes immediately on change
              if (value === "") {
                // Allow empty string temporarily but don't propagate yet
                return;
              }

              const parsed = parseFloat(value);
              if (!isNaN(parsed)) {
                const changeObj: Change = {
                  target: changeTarget,
                  operation: "update",
                  currentValue: parsed,
                  previousValue: child.quantity || 1,
                  timestamp: Date.now(),
                };
                trackChange(changeObj);
              }
            }}
            onBlur={(e) => {
              const value = e.target.value;
              // If empty string on blur, set to 0
              if (value === "") {
                setLocalQuantity("0");
                const changeObj: Change = {
                  target: changeTarget,
                  operation: "update",
                  currentValue: 0,
                  previousValue: child.quantity || 1,
                  timestamp: Date.now(),
                };
                trackChange(changeObj);
              }
            }}
            inputProps={{
              min: 0,
              step: "any",
            }}
            InputProps={{
              endAdornment:
                isItem(child.item) && child.item.item_type?.units ? (
                  <InputAdornment position="end">
                    {child.item.item_type.units.toLowerCase()}
                  </InputAdornment>
                ) : undefined,
            }}
            disabled={readOnly}
            sx={{
              "& .MuiInputBase-input": {
                fontSize: "14px",
                color: "#333",
                padding: "8px 12px",
              },
              "& .MuiOutlinedInput-root": {
                backgroundColor: isChanged
                  ? "rgba(25, 118, 210, 0.08)"
                  : "#fff",
                "&.Mui-error": {
                  borderColor: "#FB8C00 !important",
                  border: "1px solid #FB8C00 !important",
                },
                "&.Mui-error:hover": {
                  borderColor: "#FB8C00 !important",
                  border: "1px solid #FB8C00 !important",
                },
                "&.Mui-error.Mui-focused": {
                  borderColor: "#FB8C00 !important",
                  border: "1px solid #FB8C00 !important",
                },
              },
            }}
          />
          {hasViolations && violationMessage && (
            <FormHelperText
              sx={{
                color: "#FB8C00 !important",
                fontSize: "12px",
                margin: "0px 4px 0px 0px",
                maxWidth: "300px",
                whiteSpace: "initial",
              }}
            >
              {violationMessage}
            </FormHelperText>
          )}
        </FormControl>
      </TableCell>
      <TableCell sx={{ textAlign: "right" }}>
        {isItem(childItem) && (
          <>
            {isDeleted ? (
              <UndoButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onUndo(index);
                }}
                aria-label={`Undo delete ${childItem.name}`}
                disabled={readOnly}
              >
                <UndoIcon />
              </UndoButton>
            ) : (
              <Tooltip
                title={deleteDisabledReason || ""}
                placement="left"
                arrow
              >
                <span>
                  <DeleteButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(index);
                    }}
                    disabled={!canDelete || readOnly}
                    aria-label={`Delete ${childItem.name}`}
                  >
                    <Delete />
                  </DeleteButton>
                </span>
              </Tooltip>
            )}
          </>
        )}
      </TableCell>
    </StyledTableRow>
  );
};
